/**
 * Unified support inbox.
 *
 * Two ticket sources land here:
 *   1. customer_tickets — opened by customers via the portal
 *   2. platform_bugs    — auto-reported by Vloud installs (engine bugs)
 *
 * Customer tickets get the in-place reply Drawer; engine bugs link to
 * /platform-bugs.
 *
 * SLA chip — derived client-side from priority + last_activity_at:
 *    urgent ≤ 1h | high ≤ 4h | normal ≤ 24h | low ≤ 72h
 * Only meaningful for status='open'/'awaiting_staff'.
 */
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Ticket as TicketIcon, Inbox, RefreshCw, Bug, MessageCircle, Lock,
  Send, ShieldAlert, MessageSquareText, ChevronDown,
} from 'lucide-react';
import {
  AdminApiClient, 
  type CustomerTicketRow, type CustomerTicketReplyRow,
  type PlatformBugRow, type TicketStatus, type TicketPriority,
  type TicketCategory,
} from '../api';
import { userMessageFor, failureFrom, type Failure } from '../lib/api-state';
import { usePermissions } from '../lib/permissions';
import { AccessNotice } from '../components/ui/AccessNotice';
import { useSession } from '../session';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip, bandFromStatus, type Band } from '../components/ui/StatusChip';
import { DataTable, PrimaryWithSub, type Column } from '../components/ui/DataTable';
import { Drawer } from '../components/ui/Drawer';

type Source = 'customer' | 'platform';

interface UnifiedRow {
  source: Source;
  id: string;
  title: string;
  status: string;
  priority: string;
  badge: string;
  customer_id: string | null;
  updated_at: string;
  raw_customer?: CustomerTicketRow;
  raw_platform?: PlatformBugRow;
}

const SLA_TARGET_HOURS: Record<TicketPriority, number> = {
  urgent: 1, high: 4, normal: 24, low: 72,
};

export function TicketsPage(): JSX.Element {
  const { state } = useSession();
  const nav = useNavigate();
  const [tickets, setTickets] = useState<CustomerTicketRow[] | null>(null);
  const [bugs, setBugs] = useState<PlatformBugRow[] | null>(null);
  const [err, setErr] = useState<Failure | null>(null);
  const perms = usePermissions();
  const [version, setVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const [sourceFilter, setSourceFilter] = useState<'both' | 'customer' | 'platform'>('both');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<TicketCategory | 'all'>('all');

  const api = useMemo(
    () => new AdminApiClient({ baseUrl: '', getToken: () => state.token }),
    [state.token],
  );

  useEffect(() => {
    setRefreshing(true);
    Promise.all([
      api.customerTickets(filter === 'open' ? 'open_or_awaiting' : 'open')
        .then((r) => { setTickets(r.tickets); setErr(null); })
        .catch((e) => { setTickets(null); setErr(failureFrom(e, 'customer tickets')); }),
      api.platformBugs()
        .then((r) => setBugs(r.platform_bugs))
        .catch((e) => { setBugs(null); setErr((prev) => prev ?? failureFrom(e, 'engine bugs')); }),
    ]).finally(() => setRefreshing(false));
  }, [api, version, filter]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  const counts = useMemo(() => {
    let open = 0, waiting = 0, resolved = 0;
    for (const t of tickets ?? []) {
      if (t.status === 'open' || t.status === 'awaiting_staff') open += 1;
      else if (t.status === 'awaiting_customer') waiting += 1;
      else if (t.status === 'resolved' || t.status === 'closed') resolved += 1;
    }
    return { open, waiting, resolved };
  }, [tickets]);

  const rows = useMemo<UnifiedRow[]>(() => {
    const merged: UnifiedRow[] = [];
    if (tickets && (sourceFilter === 'both' || sourceFilter === 'customer')) {
      for (const t of tickets) {
        if (filter === 'open' && (t.status === 'closed' || t.status === 'resolved')) continue;
        if (priorityFilter !== 'all' && t.priority !== priorityFilter) continue;
        if (categoryFilter !== 'all' && t.category !== categoryFilter) continue;
        merged.push({
          source: 'customer',
          id: t.id,
          title: t.subject,
          status: t.status,
          priority: t.priority,
          badge: t.category,
          customer_id: t.customer_id,
          updated_at: t.last_activity_at,
          raw_customer: t,
        });
      }
    }
    if (bugs && (sourceFilter === 'both' || sourceFilter === 'platform')) {
      // Engine bugs don't carry customer-ticket priority/category, so they
      // only appear when both filters are 'all'.
      const includesEngine = priorityFilter === 'all' && categoryFilter === 'all';
      if (includesEngine) {
        for (const b of bugs) {
          if (filter === 'open' && (b.status === 'fixed' || b.status === 'verified' || b.status === 'wontfix')) continue;
          merged.push({
            source: 'platform',
            id: b.id,
            title: b.summary,
            status: b.status,
            priority: 'auto',
            badge: `engine bug · ${b.binary_version}`,
            customer_id: null,
            updated_at: b.last_seen_at,
            raw_platform: b,
          });
        }
      }
    }
    merged.sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at));
    return merged;
  }, [tickets, bugs, filter, sourceFilter, priorityFilter, categoryFilter]);

  const priorityCounts = useMemo(() => {
    const c: Record<TicketPriority | 'all', number> = { all: 0, urgent: 0, high: 0, normal: 0, low: 0 };
    for (const t of tickets ?? []) {
      c.all += 1;
      c[t.priority] = (c[t.priority] ?? 0) + 1;
    }
    return c;
  }, [tickets]);

  const categoryCounts = useMemo(() => {
    const c: Record<TicketCategory | 'all', number> = {
      all: 0, support: 0, billing: 0, feature_request: 0, bug_report: 0, other: 0,
    };
    for (const t of tickets ?? []) {
      c.all += 1;
      c[t.category] = (c[t.category] ?? 0) + 1;
    }
    return c;
  }, [tickets]);

  const sourceChips: Array<{ key: 'both' | 'customer' | 'platform'; label: string; count: number }> = [
    { key: 'both',     label: 'All',      count: rows.length },
    { key: 'customer', label: 'Customer', count: rows.filter((r) => r.source === 'customer').length },
    { key: 'platform', label: 'Engine',   count: rows.filter((r) => r.source === 'platform').length },
  ];

  const columns: Column<UnifiedRow>[] = [
    {
      key: 'source',
      header: '',
      width: 40,
      cell: (r) => <SourceIcon source={r.source} />,
    },
    {
      key: 'subject',
      header: 'Subject',
      sortable: true,
      cell: (r) => <PrimaryWithSub primary={r.title} sub={r.badge} />,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <StatusChip band={bandFromStatus(r.status)}>
          {r.status.replace(/_/g, ' ')}
        </StatusChip>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      cell: (r) =>
        r.source === 'platform' ? (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>auto</span>
        ) : (
          <StatusChip band={priorityBand(r.priority as TicketPriority)} noDot size="sm">
            {r.priority}
          </StatusChip>
        ),
    },
    {
      key: 'sla',
      header: 'SLA',
      cell: (r) =>
        r.source === 'customer' && r.raw_customer
          ? <SlaChip ticket={r.raw_customer} />
          : <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>—</span>,
    },
    {
      key: 'updated',
      header: 'Updated',
      align: 'right',
      sortable: true,
      cell: (r) => (
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {fmtRelative(r.updated_at)}
        </span>
      ),
    },
  ];

  const onRowClick = (r: UnifiedRow): void => {
    if (r.source === 'customer') setOpenTicketId(r.id);
    else nav('/platform-bugs');
  };

  return (
    <div className="page">
      <PageHeader
        title="Tickets"
        subtitle="Customer support tickets + auto-reported engine bugs in one queue."
        icon={TicketIcon}
        accent="#22d3ee"
        actions={
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw width={14} height={14}
              style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
            Refresh
          </Button>
        }
      />

      <AccessNotice reason={perms.canWrite ? null : `${perms.writeReason ?? ''} Replying to and triaging tickets is not available to you.`} />

      {tickets && <HeroPanel
        icon={Inbox}
        accent="#22d3ee"
        title="Support inbox"
        subtitle="Open queue, in-flight conversations, and resolved threads from the last reload."
        kpis={[
          <HeroKpi key="o" label="Open" value={counts.open}
                   band={counts.open > 0 ? 'critical' : 'healthy'} />,
          <HeroKpi key="w" label="Awaiting customer" value={counts.waiting}
                   band={counts.waiting > 0 ? 'degraded' : 'neutral'}
                   hint="ball is in their court" />,
          <HeroKpi key="r" label="Resolved" value={counts.resolved} band={counts.resolved > 0 ? 'healthy' : 'neutral'} />,
        ]}
      />}

      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Toolbar
          filter={filter} onFilter={setFilter}
          sourceFilter={sourceFilter} onSourceFilter={setSourceFilter}
          sourceChips={sourceChips}
          priorityFilter={priorityFilter} onPriorityFilter={setPriorityFilter}
          priorityCounts={priorityCounts}
          categoryFilter={categoryFilter} onCategoryFilter={setCategoryFilter}
          categoryCounts={categoryCounts}
        />

        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => `${r.source}-${r.id}`}
          onRowClick={onRowClick}
          loading={!tickets && !bugs && !err}
          error={err}
          onRetry={onRefresh}
          emptyTitle="No tickets in this view"
          emptyDescription="Try different filters, or hold tight while customers send more."
          emptyIcon={MessageCircle}
        />
      </div>

      {openTicketId && (
        <CustomerTicketDrawer
          api={api}
          canWrite={perms.canWrite}
          id={openTicketId}
          onClose={() => setOpenTicketId(null)}
          onChange={onRefresh}
        />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Toolbar
 * ────────────────────────────────────────────────────────────────── */

function Toolbar({
  filter, onFilter,
  sourceFilter, onSourceFilter, sourceChips,
  priorityFilter, onPriorityFilter, priorityCounts,
  categoryFilter, onCategoryFilter, categoryCounts,
}: {
  filter: 'open' | 'all'; onFilter: (v: 'open' | 'all') => void;
  sourceFilter: 'both' | 'customer' | 'platform';
  onSourceFilter: (v: 'both' | 'customer' | 'platform') => void;
  sourceChips: Array<{ key: 'both' | 'customer' | 'platform'; label: string; count: number }>;
  priorityFilter: TicketPriority | 'all';
  onPriorityFilter: (v: TicketPriority | 'all') => void;
  priorityCounts: Record<TicketPriority | 'all', number>;
  categoryFilter: TicketCategory | 'all';
  onCategoryFilter: (v: TicketCategory | 'all') => void;
  categoryCounts: Record<TicketCategory | 'all', number>;
}): JSX.Element {
  const priorityChips: Array<{ key: TicketPriority | 'all'; label: string; count: number }> = [
    { key: 'all',    label: 'All',    count: priorityCounts.all },
    { key: 'urgent', label: 'Urgent', count: priorityCounts.urgent },
    { key: 'high',   label: 'High',   count: priorityCounts.high },
    { key: 'normal', label: 'Normal', count: priorityCounts.normal },
    { key: 'low',    label: 'Low',    count: priorityCounts.low },
  ];
  const categoryChips: Array<{ key: TicketCategory | 'all'; label: string; count: number }> = [
    { key: 'all',             label: 'Any',          count: categoryCounts.all },
    { key: 'support',         label: 'Support',      count: categoryCounts.support },
    { key: 'billing',         label: 'Billing',      count: categoryCounts.billing },
    { key: 'feature_request', label: 'Feature req.', count: categoryCounts.feature_request },
    { key: 'bug_report',      label: 'Bug report',   count: categoryCounts.bug_report },
    { key: 'other',           label: 'Other',        count: categoryCounts.other },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Segmented
          chips={[
            { key: 'open', label: 'Open + waiting' },
            { key: 'all',  label: 'All' },
          ]}
          value={filter}
          onChange={onFilter}
        />
        <SegmentedWithCounts
          chips={sourceChips}
          value={sourceFilter}
          onChange={onSourceFilter}
        />
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <FilterCluster label="Priority">
          <SegmentedWithCounts chips={priorityChips} value={priorityFilter} onChange={onPriorityFilter} />
        </FilterCluster>
        <FilterCluster label="Category">
          <SegmentedWithCounts chips={categoryChips} value={categoryFilter} onChange={onCategoryFilter} />
        </FilterCluster>
      </div>
    </div>
  );
}

function FilterCluster({
  label, children,
}: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <span
        style={{
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--text-muted)',
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

function Segmented<T extends string>({
  chips, value, onChange,
}: {
  chips: Array<{ key: T; label: string }>;
  value: T;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Filters"
      style={{
        display: 'inline-flex', gap: 4, padding: 4,
        borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-overlay)',
      }}
    >
      {chips.map((c) => {
        const active = c.key === value;
        return (
          <button
            key={c.key}
            aria-pressed={active}
            onClick={() => onChange(c.key)}
            style={{
              padding: '6px 12px', border: 'none', borderRadius: 7,
              background: active ? 'var(--bg-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer',
            }}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

function SegmentedWithCounts<T extends string>({
  chips, value, onChange,
}: {
  chips: Array<{ key: T; label: string; count: number }>;
  value: T;
  onChange: (next: T) => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Filters"
      style={{
        display: 'inline-flex', gap: 4, padding: 4,
        borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-overlay)',
      }}
    >
      {chips.map((c) => {
        const active = c.key === value;
        return (
          <button
            key={c.key}
            aria-pressed={active}
            onClick={() => onChange(c.key)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '6px 10px', border: 'none', borderRadius: 7,
              background: active ? 'var(--bg-raised)' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-muted)',
              fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer',
            }}
          >
            {c.label}
            <span
              style={{
                fontSize: '0.6875rem',
                color: 'var(--text-muted)',
                background: 'var(--bg-overlay)',
                padding: '0 6px', borderRadius: 999,
                border: '1px solid var(--border)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >{c.count}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Source icon + Priority + SLA
 * ────────────────────────────────────────────────────────────────── */

function SourceIcon({ source }: { source: Source }): JSX.Element {
  const Icon = source === 'customer' ? MessageCircle : Bug;
  const tone = source === 'customer' ? '#22d3ee' : '#f87171';
  return (
    <span
      title={source === 'customer' ? 'Customer ticket' : 'Engine bug'}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 26, height: 26, borderRadius: 8,
        background: `${tone}1a`, color: tone,
      }}
    >
      <Icon width={13} height={13} />
    </span>
  );
}

function priorityBand(p: TicketPriority): Band {
  switch (p) {
    case 'urgent': return 'critical';
    case 'high':   return 'warning';
    case 'normal': return 'neutral';
    case 'low':    return 'neutral';
  }
}

function SlaChip({ ticket }: { ticket: CustomerTicketRow }): JSX.Element {
  const isLive = ticket.status === 'open' || ticket.status === 'awaiting_staff';
  if (!isLive) {
    return <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>—</span>;
  }
  const targetH = SLA_TARGET_HOURS[ticket.priority] ?? 24;
  const ageH = (Date.now() - new Date(ticket.last_activity_at).getTime()) / 1000 / 3600;
  const remaining = targetH - ageH;
  let band: Band = 'healthy';
  let label = '';
  if (remaining < 0) {
    band = 'critical';
    label = `${fmtH(-remaining)} over`;
  } else if (remaining < targetH * 0.25) {
    band = 'warning';
    label = `${fmtH(remaining)} left`;
  } else {
    band = 'healthy';
    label = `${fmtH(remaining)} left`;
  }
  return (
    <StatusChip band={band} size="sm">
      {label}
    </StatusChip>
  );
}

function fmtH(hours: number): string {
  if (hours < 1) {
    const m = Math.max(1, Math.round(hours * 60));
    return `${m}m`;
  }
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/* ──────────────────────────────────────────────────────────────────
 * Drawer with thread + customer panel + reply
 * ────────────────────────────────────────────────────────────────── */

const TICKET_STATUSES: TicketStatus[] = ['open', 'awaiting_customer', 'awaiting_staff', 'resolved', 'closed'];
const TICKET_PRIORITIES: TicketPriority[] = ['low', 'normal', 'high', 'urgent'];

function CustomerTicketDrawer({
  api, id, canWrite, onClose, onChange,
}: {
  api: AdminApiClient;
  canWrite: boolean;
  id: string;
  onClose: () => void;
  onChange: () => void;
}): JSX.Element {
  const [thread, setThread] = useState<{ ticket: CustomerTicketRow; replies: CustomerTicketReplyRow[] } | null>(null);
  const [draft, setDraft] = useState('');
  const [internal, setInternal] = useState(false);
  const [nextStatus, setNextStatus] = useState<TicketStatus | ''>('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const lastIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastIdRef.current === id) return;
    lastIdRef.current = id;
    setThread(null);
    setErr(null);
    api.customerTicket(id)
      .then(setThread)
      .catch((e) => setErr(failureFrom(e, 'this ticket').message));
  }, [api, id]);

  const send = async (): Promise<void> => {
    if (!draft.trim()) return;
    setSending(true);
    setErr(null);
    try {
      await api.replyToCustomerTicket(id, {
        body: draft,
        internal,
        next_status: nextStatus === '' ? undefined : (nextStatus as TicketStatus),
      });
      setDraft('');
      setInternal(false);
      setNextStatus('');
      const refreshed = await api.customerTicket(id);
      setThread(refreshed);
      onChange();
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setSending(false);
    }
  };

  const triage = async (
    body: { status?: TicketStatus; priority?: TicketPriority; assigned_to?: string | null },
  ): Promise<void> => {
    try {
      const updated = await api.triageCustomerTicket(id, body);
      setThread((t) => (t ? { ...t, ticket: updated } : t));
      onChange();
    } catch (e) {
      setErr(userMessageFor(e));
    }
  };

  return (
    <Drawer
      open
      onClose={() => { lastIdRef.current = null; onClose(); }}
      width="lg"
      title={thread?.ticket.subject ?? 'Loading…'}
      subtitle={thread ? `Customer ${thread.ticket.customer_id.slice(0, 8)}…` : undefined}
      footer={
        thread && !canWrite ? (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            You can read this conversation. Replying and changing status are available to Owner and Admin accounts.
          </span>
        ) : thread && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              <input
                type="checkbox"
                checked={internal}
                onChange={(e) => setInternal(e.target.checked)}
              />
              Internal
            </label>
            <select
              value={nextStatus}
              onChange={(e) => setNextStatus(e.target.value as TicketStatus | '')}
              style={{
                padding: '6px 8px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--bg-overlay)',
                color: 'var(--text-primary)', fontSize: '0.75rem',
              }}
            >
              <option value="">Auto-flip status</option>
              {TICKET_STATUSES.map((s) => (
                <option key={s} value={s}>→ {s.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <Button
              variant="default"
              size="sm"
              onClick={() => void send()}
              disabled={sending || !draft.trim()}
              style={{ marginLeft: 'auto' }}
            >
              <Send width={14} height={14} />
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        )
      }
    >
      {err && <p className="err" role="alert" style={{ fontSize: '0.8125rem' }}>{err}</p>}
      {!thread && !err && <p role="status" style={{ color: 'var(--text-muted)' }}>Loading…</p>}

      {thread && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <CustomerPanel ticket={thread.ticket} canWrite={canWrite} onTriage={(b) => void triage(b)} />

          <Thread initial={thread.ticket} replies={thread.replies} />

          {canWrite && <div>
            <div
              style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 8,
                marginBottom: 8,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: '0.6875rem', fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                  color: 'var(--text-muted)',
                }}
              >
                Reply
              </h3>
              <SavedRepliesMenu
                customerName={`Customer ${thread.ticket.customer_id.slice(0, 8)}…`}
                onPick={(text) => setDraft((d) => d ? `${d}\n\n${text}` : text)}
              />
            </div>
            <textarea
              rows={5}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Reply to the customer (or check internal for a private staff note)…"
              maxLength={50_000}
              style={{
                width: '100%',
                padding: 10,
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--bg-overlay)',
                color: 'var(--text-primary)',
                fontSize: '0.8125rem',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </div>}
        </div>
      )}
    </Drawer>
  );
}

function CustomerPanel({
  ticket, canWrite, onTriage,
}: {
  ticket: CustomerTicketRow;
  canWrite: boolean;
  onTriage: (b: { status?: TicketStatus; priority?: TicketPriority; assigned_to?: string | null }) => void;
}): JSX.Element {
  return (
    <section
      style={{
        padding: 14,
        borderRadius: 12,
        background: 'var(--bg-overlay)',
        border: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div
          style={{
            width: 36, height: 36, borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(34, 211, 238, 0.15)',
            border: '1px solid rgba(34, 211, 238, 0.35)',
            color: '#22d3ee',
          }}
        >
          <ShieldAlert width={16} height={16} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.8125rem', fontWeight: 600 }}>
            Customer {ticket.customer_id.slice(0, 8)}…
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            opened {new Date(ticket.created_at).toLocaleString()}
            {ticket.license_id && <> · license {ticket.license_id.slice(0, 8)}…</>}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
        <Field label="Status">
          <select
            aria-label="Status"
            disabled={!canWrite}
            value={ticket.status}
            onChange={(e) => onTriage({ status: e.target.value as TicketStatus })}
            style={selectStyle}
          >
            {TICKET_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </Field>
        <Field label="Priority">
          <select
            aria-label="Priority"
            disabled={!canWrite}
            value={ticket.priority}
            onChange={(e) => onTriage({ priority: e.target.value as TicketPriority })}
            style={selectStyle}
          >
            {TICKET_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Assigned to">
          <input
            aria-label="Assigned to"
            readOnly={!canWrite}
            defaultValue={ticket.assigned_to ?? ''}
            placeholder="staff handle"
            onBlur={(e) => {
              const next = e.target.value.trim() === '' ? null : e.target.value.trim();
              // Only send a change; a plain blur must not write.
              if (!canWrite || next === (ticket.assigned_to ?? null)) return;
              onTriage({ assigned_to: next });
            }}
            style={selectStyle}
          />
        </Field>
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{label}</span>
      {children}
    </div>
  );
}

const selectStyle = {
  width: '100%',
  padding: '6px 8px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg-raised)',
  color: 'var(--text-primary)',
  fontSize: '0.75rem',
  outline: 'none',
} as const;

function Thread({
  initial, replies,
}: { initial: CustomerTicketRow; replies: CustomerTicketReplyRow[] }): JSX.Element {
  return (
    <section>
      <h3
        style={{
          margin: 0, marginBottom: 8,
          fontSize: '0.6875rem', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.08em',
          color: 'var(--text-muted)',
        }}
      >
        Conversation
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Bubble
          role="customer"
          label="Customer"
          time={initial.created_at}
          body={initial.body}
        />
        {replies.map((r) => (
          <Bubble
            key={r.id}
            role={r.author_type === 'staff' ? (r.internal ? 'internal' : 'staff') : 'customer'}
            label={r.author_type === 'staff' ? (r.internal ? 'Staff · internal' : 'Staff') : 'Customer'}
            time={r.created_at}
            body={r.body}
          />
        ))}
      </div>
    </section>
  );
}

function Bubble({
  role, label, time, body,
}: {
  role: 'customer' | 'staff' | 'internal';
  label: string;
  time: string;
  body: string;
}): JSX.Element {
  const tones: Record<typeof role, { bg: string; bd: string; fg: string }> = {
    customer: { bg: 'var(--bg-overlay)', bd: 'var(--border)', fg: 'var(--text-primary)' },
    staff:    { bg: 'rgba(99, 102, 241, 0.10)', bd: 'rgba(99, 102, 241, 0.35)', fg: 'var(--text-primary)' },
    internal: { bg: 'rgba(245, 158, 11, 0.10)', bd: 'rgba(245, 158, 11, 0.35)', fg: 'var(--text-primary)' },
  };
  const tone = tones[role];
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 10,
        background: tone.bg,
        border: `1px solid ${tone.bd}`,
      }}
    >
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: '0.6875rem', color: 'var(--text-muted)',
          marginBottom: 6,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 500 }}>
          {role === 'internal' && <Lock width={11} height={11} />}
          {label}
        </span>
        <span>{new Date(time).toLocaleString()}</span>
      </div>
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          margin: 0,
          fontFamily: 'inherit',
          fontSize: '0.8125rem',
          color: tone.fg,
          lineHeight: 1.5,
        }}
      >
        {body}
      </pre>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Saved replies menu (pre-canned templates)
 *
 * Templates are local constants; if/when /internal/saved-replies lands
 * we'd swap this for a fetched list.
 * ────────────────────────────────────────────────────────────────── */

interface SavedReply {
  id: string;
  label: string;
  body: (vars: { customerName: string }) => string;
}

const SAVED_REPLIES: SavedReply[] = [
  {
    id: 'ack',
    label: 'Acknowledge',
    body: ({ customerName }) =>
      `Hi ${customerName.split(' ')[0]},\n\nThanks for flagging this — I've picked up the ticket and I'm digging in. I'll follow up once I have something concrete.`,
  },
  {
    id: 'need_info',
    label: 'Need more info',
    body: () =>
      `Could you share:\n• your license id\n• the engine version (visible in the dashboard footer)\n• the steps that led to the issue\n\nThat'll let us reproduce on our side.`,
  },
  {
    id: 'investigating',
    label: 'Investigating',
    body: () =>
      `Confirmed on our end — the team is investigating. I'll update this ticket within 24 hours, sooner if there's news.`,
  },
  {
    id: 'refund_initiated',
    label: 'Refund initiated',
    body: () =>
      `I've issued the refund — it'll show up on your statement within 5–10 business days, depending on your bank.`,
  },
  {
    id: 'fix_shipping',
    label: 'Fix shipping',
    body: () =>
      `A fix is in the next release; the engine will pick it up automatically on its next update-check tick. No action required from your side.`,
  },
  {
    id: 'closing',
    label: 'Closing',
    body: () =>
      `Glad we got this sorted! I'm marking the ticket resolved — feel free to reopen (or open a new one) if anything else comes up.`,
  },
];

function SavedRepliesMenu({
  customerName, onPick,
}: { customerName: string; onPick: (text: string) => void }): JSX.Element {
  const [open, setOpen] = useState(false);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    const handler = (): void => setOpen(false);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [open]);

  return (
    <div
      style={{ position: 'relative' }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '4px 8px', borderRadius: 6,
          border: '1px solid var(--border)',
          background: 'var(--bg-overlay)',
          color: 'var(--text-muted)',
          fontSize: '0.6875rem', fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        <MessageSquareText width={11} height={11} />
        Saved replies
        <ChevronDown width={11} height={11} />
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            zIndex: 20,
            minWidth: 220,
            padding: 4,
            borderRadius: 10,
            background: 'var(--bg-raised)',
            border: '1px solid var(--border)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
            display: 'flex', flexDirection: 'column', gap: 2,
          }}
        >
          {SAVED_REPLIES.map((r) => (
            <button
              key={r.id}
              onClick={() => { onPick(r.body({ customerName })); setOpen(false); }}
              style={{
                textAlign: 'left',
                padding: '6px 10px',
                background: 'transparent',
                border: 'none',
                borderRadius: 6,
                color: 'var(--text-primary)',
                fontSize: '0.75rem',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'var(--bg-overlay)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

